import { createContext, useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { tokenStore } from '../services/tokenStore'
import { refreshTokenStore } from '../services/refreshTokenStore'
import { SESSION_REFRESH_TOKEN_KEY, SESSION_TOKEN_KEY, SESSION_USER_KEY } from '../services/sessionKeys'
import type { LoginUser } from '../types/api'
import axiosInstance from '../services/axiosInstance'
import { parseMeResponse } from '../services/parseLoginResponse'
import { hasPermission as checkPermission, normalizePermissions } from '../utils/permissions'
import type { Permission } from '../types/permissions'

// ── Types ──────────────────────────────────────────────────────────────────

export interface AuthContextValue {
  user: LoginUser | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (user: LoginUser, accessToken: string, refreshToken: string) => Promise<void>
  logout: () => void
  hasPermission: (permission: Permission) => boolean
}

// ── Session persistence (survives page refresh, cleared on tab close) ──────

const persistSession = (user: LoginUser, accessToken: string, refreshToken: string): void => {
  sessionStorage.setItem(SESSION_USER_KEY, JSON.stringify(user))
  sessionStorage.setItem(SESSION_TOKEN_KEY, accessToken)
  sessionStorage.setItem(SESSION_REFRESH_TOKEN_KEY, refreshToken)
  refreshTokenStore.set(refreshToken)
}

const clearSession = (): void => {
  sessionStorage.removeItem(SESSION_USER_KEY)
  sessionStorage.removeItem(SESSION_TOKEN_KEY)
  sessionStorage.removeItem(SESSION_REFRESH_TOKEN_KEY)
  refreshTokenStore.clear()
}

const rehydrateSession = (): { user: LoginUser; token: string; refreshToken: string } | null => {
  try {
    const raw = sessionStorage.getItem(SESSION_USER_KEY)
    const token = sessionStorage.getItem(SESSION_TOKEN_KEY)
    const refreshToken = sessionStorage.getItem(SESSION_REFRESH_TOKEN_KEY) ?? ''
    if (!raw || !token) return null

    return { user: JSON.parse(raw) as LoginUser, token, refreshToken }
  } catch {
    return null
  }
}

const normalizeAuthUser = (authUser: LoginUser): LoginUser => ({
  ...authUser,
  permissions: normalizePermissions(authUser.permissions),
})

// ── Context ────────────────────────────────────────────────────────────────

export const AuthContext = createContext<AuthContextValue | null>(null)

// Shared reference so axiosInstance can call logout without circular imports
export const authActions = {
  logout: (): void => {
    /* wired at runtime by AuthProvider */
  },
}

export const AuthProvider = ({ children }: { children: ReactNode }): React.JSX.Element => {
  const [user, setUser] = useState<LoginUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // On mount: rehydrate from sessionStorage, then validate token via /me
  useEffect(() => {
    const session = rehydrateSession()
    if (!session) {
      setIsLoading(false)
      return
    }

    tokenStore.set(session.token)
    refreshTokenStore.set(session.refreshToken)
    const normalizedSessionUser = normalizeAuthUser(session.user)
    setUser(normalizedSessionUser)

    // Refresh user info from server — also validates the token is still active
    axiosInstance
      .get<unknown>('/api/v1/auth/me')
      .then(({ data }) => {
        const meUser = parseMeResponse(data)
        if (!meUser) {
          tokenStore.clear()
          clearSession()
          setUser(null)
          return
        }

        const normalizedUser = normalizeAuthUser(meUser)
        persistSession(normalizedUser, session.token, session.refreshToken)
        setUser(normalizedUser)
      })
      .catch(() => {
        tokenStore.clear()
        clearSession()
        setUser(null)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [])

  const login = useCallback(async (_authUser: LoginUser, accessToken: string, refreshToken: string): Promise<void> => {
    tokenStore.set(accessToken)
    refreshTokenStore.set(refreshToken)

    try {
      const { data } = await axiosInstance.get<unknown>('/api/v1/auth/me')
      const meUser = parseMeResponse(data)
      if (!meUser) {
        tokenStore.clear()
        refreshTokenStore.clear()
        clearSession()
        setUser(null)
        throw new Error('Unexpected profile response from server.')
      }

      const normalizedUser = normalizeAuthUser(meUser)
      persistSession(normalizedUser, accessToken, refreshToken)
      setUser(normalizedUser)
    } catch (error) {
      tokenStore.clear()
      refreshTokenStore.clear()
      clearSession()
      setUser(null)
      throw error
    }
  }, [])

  const logout = useCallback((): void => {
    // Call logout API first, then clear session and redirect on success
    axiosInstance.post('/api/v1/auth/logout').finally(() => {
      // Clear regardless of success or failure
      tokenStore.clear()
      clearSession()
      setUser(null)
    })
  }, [])

  authActions.logout = logout

  const hasPermission = useCallback(
    (permission: Permission): boolean => {
      if (!user) return false

      // Super admin always has full access
      if (user.role.name === 'SUPER_ADMIN') return true

      const userPermissions = normalizePermissions(user.permissions)
      return checkPermission(userPermissions, permission)
    },
    [user],
  )

  const value = useMemo<AuthContextValue>(
    () => ({ user, isAuthenticated: user !== null, isLoading, login, logout, hasPermission }),
    [user, isLoading, login, logout, hasPermission],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
